import { Country } from 'country-state-city';

// ISO Alpha-2 codes that pycountry recognizes
// This list should be maintained and updated when pycountry updates
const PYCOUNTRY_ISO_CODES = new Set([
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW'
]);

// Countries that exist in country-state-city but are NOT recognized by pycountry
// These will be explicitly excluded from the filtered list
const INCOMPATIBLE_COUNTRIES = new Set<string>([
  // Add any countries that exist in country-state-city but not in pycountry
  // This list should be updated if new incompatibilities are discovered
]);

/**
 * Maps country names from country-state-city to pycountry-compatible names
 * Handles known naming discrepancies between the two packages
 */
const COUNTRY_NAME_MAPPING: Record<string, string> = {
  'Virgin Islands (British)': 'Virgin Islands, British',
  'Virgin Islands (U.S.)': 'Virgin Islands, U.S.',
  'Korea (Republic of)': 'Korea, Republic of',
  "Korea (Democratic People's Republic of)": "Korea, Democratic People's Republic of",
  'Russian Federation': 'Russian Federation',
  'Iran (Islamic Republic of)': 'Iran, Islamic Republic of',
  'Venezuela (Bolivarian Republic of)': 'Venezuela, Bolivarian Republic of',
  'Bolivia (Plurinational State of)': 'Bolivia, Plurinational State of',
  'Moldova (Republic of)': 'Moldova, Republic of',
  'Tanzania, United Republic of': 'Tanzania, United Republic of',
  'Congo (Democratic Republic of the)': 'Congo, Democratic Republic of the',
  Congo: 'Congo',
  'Taiwan, Province of China': 'Taiwan, Province of China',
  'Palestine, State of': 'Palestine, State of',
  'Syrian Arab Republic': 'Syrian Arab Republic',
  "Lao People's Democratic Republic": "Lao People's Democratic Republic",
  'Micronesia (Federated States of)': 'Micronesia, Federated States of',
  'Macedonia (the former Yugoslav Republic of)': 'Macedonia, the former Yugoslav Republic of'
};

/**
 * Filters countries from country-state-city to only include those
 * recognized by pycountry (based on ISO Alpha-2 codes and known incompatibilities)
 */
export function getPycountryCompatibleCountries() {
  return Country.getAllCountries().filter((country) => {
    // First check if the ISO code is recognized by pycountry
    if (!PYCOUNTRY_ISO_CODES.has(country.isoCode)) {
      return false;
    }

    // Then check if this country is explicitly marked as incompatible
    if (INCOMPATIBLE_COUNTRIES.has(country.name)) {
      return false;
    }

    // If we have a mapping for this country, it's definitely compatible
    if (COUNTRY_NAME_MAPPING[country.name]) {
      return true;
    }

    // For countries without explicit mappings, we assume they're compatible
    // unless they're in the incompatible list
    return true;
  });
}

/**
 * Gets the pycountry-compatible country name
 */
export function getPycountryCompatibleCountryName(countryName: string): string {
  return COUNTRY_NAME_MAPPING[countryName] || countryName;
}

/**
 * Creates a reverse mapping from pycountry names back to country-state-city names
 */
const REVERSE_COUNTRY_NAME_MAPPING: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_NAME_MAPPING).map(([key, value]) => [value, key])
);

/**
 * Converts a pycountry country name back to its country-state-city equivalent
 * This is used when displaying country names from the backend
 * Also supports alpha2 country codes as input
 */
export function getCountryStateCityCompatibleCountryName(pycountryName: string): string {
  // Check if the input is an alpha2 country code (2 uppercase letters)
  if (pycountryName && pycountryName.length === 2 && /^[A-Z]{2}$/.test(pycountryName)) {
    // Find the country by ISO code and return its country-state-city name
    const country = Country.getAllCountries().find((c) => c.isoCode === pycountryName);
    if (country) {
      return country.name;
    }
  }

  // Handle pycountry name mapping
  return REVERSE_COUNTRY_NAME_MAPPING[pycountryName] || pycountryName;
}
